import React, { useState } from 'react';
import { ArrowRight, Check, Minus } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';
import { FEATURES, PLANS, planIncludes } from './plans';
import usePlanStart from './usePlanStart';

const PricingTable = () => {
  const { t } = useLanguage();
  const startPlan = usePlanStart();
  const [selected, setSelected] = useState(
    () => (PLANS.find((plan) => plan.featured) || PLANS[0]).key
  );

  const includedCount = FEATURES.filter((feature) => planIncludes(selected, feature.from)).length;
  const countText = t('pricing_features_count')
    .replace('{count}', includedCount)
    .replace('{total}', FEATURES.length);

  return (
    <section className="section-band">
      <div className="section-band__inner">
        <div className="section-band__head">
          <SectionEyebrow>{t('pricing_comparison_title')}</SectionEyebrow>
          <p className="section-band__lead">{t('pricing_compare_lead')}</p>
        </div>

        <div className="tp-plan-picker" role="group" aria-label={t('pricing_compare_pick')}>
          {PLANS.map((plan) => {
            const isSelected = plan.key === selected;
            return (
              <button
                key={plan.key}
                type="button"
                className={`tp-plan-option ${isSelected ? 'is-selected' : ''}`.trim()}
                aria-pressed={isSelected}
                onClick={() => setSelected(plan.key)}
              >
                <span className="tp-plan-option__top">
                  <span className="tp-plan-option__name">{t(`pricing_${plan.key}`)}</span>
                  {plan.featured && (
                    <span className="tp-plan-option__badge">{t('pricing_most_popular')}</span>
                  )}
                  <span className="tp-plan-option__mark" aria-hidden="true">
                    <Check size={12} strokeWidth={3} />
                  </span>
                </span>
                <span className="tp-plan-option__price">
                  <span className="tp-plan-option__currency">€</span>
                  <span className="tp-plan-option__amount">{plan.price}</span>
                  <span className="tp-plan-option__period">/ {t('pricing_month')}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="tp-compare">
          <table className="tp-compare__table">
            <thead>
              <tr>
                <th scope="col" className="tp-compare__feature-head">{t('pricing_features')}</th>
                {PLANS.map((plan) => (
                  <th
                    key={plan.key}
                    scope="col"
                    className={plan.key === selected ? 'is-selected' : undefined}
                  >
                    {t(`pricing_${plan.key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((feature) => (
                <tr
                  key={feature.key}
                  className={planIncludes(selected, feature.from) ? 'is-included' : 'is-excluded'}
                >
                  <th scope="row">{t(feature.key)}</th>
                  {PLANS.map((plan) => (
                    <td
                      key={plan.key}
                      className={plan.key === selected ? 'is-selected' : undefined}
                    >
                      {planIncludes(plan.key, feature.from) ? (
                        <Check className="tp-compare__yes" size={18} strokeWidth={2.5} aria-label={t('pricing_included')} />
                      ) : (
                        <Minus className="tp-compare__no" size={16} strokeWidth={2} aria-label={t('pricing_not_included')} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="tp-compare-mobile">
          {FEATURES.map((feature) => {
            const included = planIncludes(selected, feature.from);
            return (
              <li key={feature.key} className={included ? 'is-included' : 'is-excluded'}>
                {included ? (
                  <Check className="tp-compare__yes" size={16} strokeWidth={2.5} aria-label={t('pricing_included')} />
                ) : (
                  <Minus className="tp-compare__no" size={14} strokeWidth={2} aria-label={t('pricing_not_included')} />
                )}
                <span>{t(feature.key)}</span>
              </li>
            );
          })}
        </ul>

        <div className="tp-compare-cta">
          <p className="tp-compare-cta__summary" aria-live="polite">
            <strong>{t(`pricing_${selected}`)}</strong> · {countText}
          </p>
          <button
            type="button"
            className="btn btn--primary tp-compare-cta__button"
            onClick={() => startPlan(selected)}
          >
            {t(`pricing_${selected}_btn`)}
            <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PricingTable;
